import { gql, useQuery, useMutation } from '@apollo/client'
import GolferProfileFragment from 'fragments/student/golferProfile'
import { useEffect, useState } from 'react'

const GET_PRACTICE_SESSION_TYPES = gql`
  query practiceSessionTypes(
    $slug: String
    $startDateTime: ISO8601DateTime
    $endDateTime: ISO8601DateTime
  ) {
    academy(slug: $slug) {
      practiceSessionTypes {
        id
        description
        title
        durationInMinutes
        priceInCents
        spaces {
          id
          name
          description
          capacity
          facility {
            id
            name
            timezone
            city
            state
            zip
          }
        }
        availableTimes(startDateTime: $startDateTime, endDateTime: $endDateTime)
      }
      id
      name
      facilities {
        id
        name
        timezone
        city
        state
        zip
      }
      coaches {
        id
        name
        preferences {
          acceptsLeads
        }
      }
    }
  }
`

export function useGetPracticeSessionTypes({
  slug,
  startDateTime,
  endDateTime,
}) {
  return useQuery(GET_PRACTICE_SESSION_TYPES, {
    variables: {
      slug,
      startDateTime,
      endDateTime,
    },
  })
}

const GET_PRACTICE_SESSION_TAX_ESTIMATION = gql`
  query practiceSessionTaxEstimation($id: ID!) {
    practiceSessionType(id: $id) {
      id
      salesTaxInCents
    }
  }
`

export function useGetPracticeSessionTaxEstimation(
  practiceSessionTypeId,
  isPaymentIntentsEnabled,
) {
  return useQuery(GET_PRACTICE_SESSION_TAX_ESTIMATION, {
    variables: { id: practiceSessionTypeId },
    skip: !practiceSessionTypeId || isPaymentIntentsEnabled,
  })
}

export const GetStudentQuery = gql`
  ${GolferProfileFragment}
  query GetStudentQuery($id: ID!) {
    student(id: $id) {
      id
      firstName
      lastName
      golferProfile {
        ...GolferProfileAttributes
      }
    }
  }
`

export const CREATE_PRACTICE_SESSION_FOR_STUDENT = gql`
  mutation CreatePracticeSessionForStudent(
    $input: CreatePracticeSessionForStudentInput!
  ) {
    createPracticeSessionForStudent(input: $input) {
      code
      success
      message
      practiceSession {
        id
        reservation {
          spaceName
        }
      }
    }
  }
`

export const GET_STUDENTS = gql`
  query GetStudents {
    currentUser {
      students {
        id
        representatives {
          relationshipType
          contact {
            externalId
          }
        }
      }
    }
  }
`

const PREPARE_PRACTICE_SESSION_FOR_STUDENT = gql`
  mutation PrepareCreatePracticeSessionForStudent(
    $input: PrepareCreatePracticeSessionForStudentInput!
  ) {
    prepareCreatePracticeSessionForStudent(input: $input) {
      code
      success
      message
      paymentIntent {
        clientSecret
        amount
        metadata {
          operation
          args
          lineItems {
            amountInCents
            label
            collect
          }
        }
      }
    }
  }
`

const getTaxesAmountInCents = paymentIntent => {
  if (!paymentIntent?.metadata?.lineItems) return null

  const taxItem = paymentIntent.metadata.lineItems.find(
    item => item.label === 'Taxes',
  )

  return taxItem?.amountInCents || null
}

export const useGetClientSecretAndTaxes = ({
  practiceSessionTypeId,
  startDateTime,
  studentId,
  studentMemberNumber,
  isPaymentIntentsEnabled,
}) => {
  const [clientSecret, setClientSecret] = useState(null)
  const [taxesInCents, setTaxesInCents] = useState(0)
  const [errorMessage, setErrorMessage] = useState(null)

  const [
    preparePracticeSessionForStudent,
    { data, loading, error },
  ] = useMutation(PREPARE_PRACTICE_SESSION_FOR_STUDENT)

  useEffect(() => {
    setClientSecret(null)
    if (
      isPaymentIntentsEnabled &&
      practiceSessionTypeId &&
      startDateTime &&
      studentId &&
      studentMemberNumber
    ) {
      preparePracticeSessionForStudent({
        variables: {
          input: {
            practiceSessionTypeId,
            startDateTime,
            studentId,
            studentMemberNumber,
          },
        },
      })
    }
  }, [
    isPaymentIntentsEnabled,
    practiceSessionTypeId,
    startDateTime,
    studentId,
    studentMemberNumber,
    preparePracticeSessionForStudent,
  ])

  useEffect(() => {
    if (data?.prepareCreatePracticeSessionForStudent?.success) {
      const paymentIntent =
        data.prepareCreatePracticeSessionForStudent.paymentIntent
      setClientSecret(paymentIntent?.clientSecret)
      const taxes = getTaxesAmountInCents(paymentIntent)
      setTaxesInCents(taxes || 0)
    }
    if (data?.prepareCreatePracticeSessionForStudent?.success === false) {
      setErrorMessage(data.prepareCreatePracticeSessionForStudent?.message)
    }
  }, [data])
  return { clientSecret, taxesInCents, loading, error, errorMessage }
}
